import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { Image } from '../../../../models/image';
import { GalleryService } from '../../../../services/gallery.service';
import { SettingsService } from '../../../../services/settings.service';
import { InteractionService } from '../../../../services/interaction.service';
import { SynchronizationService } from '../../../../services/synchronization.service';
import { ToggleComponent } from '../toggle/toggle.component';


@Component({
  selector: 'app-gallery-ordered-view',
  templateUrl: './gallery-view-ordered.component.html',
  styleUrls: ['./gallery-view-ordered.component.scss']
  })
export class GalleryViewOrderedComponent implements OnInit, AfterViewInit, OnDestroy {
  data: any;
  @ViewChild('app-gallery-ordered-view') templateRef: TemplateRef<any>;
  @ViewChild('divcontainer') private element: ElementRef;
  public grid_columns_style: String;
  public selectedProject: String;
  public callbackOnDeleteToggle: any;
  private event: Subscription[] = [];

  // Ordered View definitions
  @ViewChild('deleteToggle', { static: true }) private deleteToggle: ToggleComponent;
  public hasImages: boolean;
  public availableImages: Image[] = [];
  public emptyImagePlaceholders: Number[] = [];
  private images: Image[] = [];
  private columnWidth = 12;
  private maxShownImages = 4;
  private startIndex = 0;

  // Initializes the viewport sizes
  private screenHeight;
  private screenWidth;
  private whRation = 33.5 / 31.5; // width-height ratio
  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

  constructor(
    private renderer: Renderer2,
    private interactionService: InteractionService,
    private galleryService: GalleryService,
    private settingsService: SettingsService,
    private synchronizationService: SynchronizationService,
  ) {
    this.onResize(); // Initializes the viewport sizes
  }

  ngOnInit(): void {
    this.initCurrentProjectAndSprint();
    this.setupPersistenceEvents();
    this.setupRealtimeEvents();
  }

  initCurrentProjectAndSprint(): void {
    this.selectedProject = this.settingsService.getProjectName();
  }

  ngAfterViewInit(): void {
    this.reloadImages();
    this.setupinteraction();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
  }

  /**
   * Loads and the images which should be displayed. Loads maximal number of displayable images at the current start index.
   * Including image at index position "start" and excluding image at index position "start + maxShownImages".
   */
  private reloadImages(): void {
    this.galleryService.getImages(this.selectedProject, this.startIndex, this.getEndIndex(), 'dateUploaded').subscribe({
      next: (images) => {

        this.hasImages = (images.length !== 0);
        if (this.hasImages) {
          this.images = images;
          this.displayImages(this.maxShownImages);
        }
      },
      error: () => {
        if (this.startIndex > 0) { // If no images are displayed (e.g. all are deleted) try to swipe left
          this.swipeLeft();
        } else {
          this.images = [];
          this.hasImages = false;
          this.displayImages(this.maxShownImages);
        }
      }
    });
  }

  /**
   * Loads the next set of images which should be displayed. Loads maximal number of displayable images at the next start index.
   * Including image at index position "start" and excluding image at index position "start + maxShownImages".
   */
  private getNextImages(): void {
    this.startIndex += this.maxShownImages;
    this.galleryService.getImages(this.selectedProject, this.startIndex, this.getEndIndex(), 'dateUploaded').subscribe({
      next: (images) => {
        if (images.length > 0) {
          this.images = images;
          this.displayImages(this.maxShownImages);
        }
      },
      error: () => {
        // reset the startIndex if there is no image available in the next image set
        this.startIndex -= this.maxShownImages;
      }
    });
  }

  /**
   * Loads the previous set of images which should be displayed. Loads maximal number of displayable images at the previous start index.
   * Including image at index position "start" and excluding image at index position "start + maxShownImages".
   */
  private getPreviousImages(): void {
    this.startIndex -= this.maxShownImages;
    if (this.startIndex < 0) {
      this.startIndex = 0;
    }

    // Call previous Images only if there are previous images available
    this.galleryService
      .getImages(this.selectedProject, this.startIndex, this.getEndIndex(), 'dateUploaded')
      .subscribe(images => {
        if (images.length > 0) {
          this.images = images;
          this.displayImages(this.maxShownImages);
        }
      });
  }

  private getEndIndex(): number {
    return this.startIndex + this.maxShownImages;
  }

  /**
   * Set up the event listeners for persistence synchronization.
   */
  private setupPersistenceEvents(): void {
    this.event.push(this.synchronizationService.subscribeEvent<Image>('uploadImage').subscribe(image => {
      this.reloadImages();
    }));
    this.event.push(this.synchronizationService.subscribeEvent<number>('deleteImage').subscribe(id => {
      this.reloadImages();
      this.removeImageFromView(id);
    }));
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealtimeEvents(): void {
    const obj = this;
    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryOrderedView${this.data['viewname']}ResizeOrderedView`)
      .subscribe(size => {
        const target = <HTMLElement>document.getElementsByClassName('gallerywidget')[0];
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
        const newSize = { newHeight: size.height, newWidth: size.width };
        this.switchNumberOfDisplayedImages(newSize);
      }));
    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryOrderedView${this.data['viewname']}SwipeLeft`)
      .subscribe(_ => {
        this.swipeLeft();
      }));
    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryOrderedView${this.data['viewname']}SwipeRight`)
      .subscribe(_ => {
        this.swipeRight();
      }));
    this.event.push(this.synchronizationService.subscribeEvent<boolean>(`GalleryOrderedView${this.data['viewname']}DeleteToggle`)
      .subscribe(val => {
        this.deleteToggle.setOnState(val);
      }));

    this.callbackOnDeleteToggle = (on) => {
      obj.synchronizationService.sendRealTimeEvent(`GalleryOrderedView${obj.data['viewname']}DeleteToggle`, on);
    };
  }

  /**
   * Set up the interactions on the widget.
   */
  private setupinteraction() {
    const html: HTMLElement = this.element.nativeElement;
    const maxHeight = html.offsetHeight * 2.4;
    const minHeight = html.offsetHeight * 1.15;
    const maxWidth = maxHeight * this.whRation;
    const minWidth = minHeight * this.whRation;
    const obj = this;

    // Overriding the resize behaviour of the widget to change dynamically the widget layout based on the size.
    this.interactionService.addPinchToResizeInteraction('.gallerywidget', (event) => {
      // Nothing to do in onStart
    }, ((size) => {
      this.switchNumberOfDisplayedImages(size);

      // Prevent that the widget can grow/shrink infinitely
      if (size.newHeight < minHeight) {
        size.newHeight = minHeight;
      } else if (size.newHeight > maxHeight) {
        size.newHeight = maxHeight;
      }
      if (size.newWidth < minWidth) {
        size.newWidth = minWidth;
      } else if (size.newWidth > maxWidth) {
        size.newWidth = maxWidth;
      }

      obj.synchronizationService.sendRealTimeEvent(`GalleryOrderedView${obj.data['viewname']}ResizeOrderedView`, {
        width: size.newWidth,
        height: size.newHeight
      });
    }), (event) => {
      // Nothing to do in onEnd
    }, this.whRation);
  }

  /**
   * Switches the number of the displayed images when the new size of the resized Element exceeds or undercuts a threshold.
   * @param size The new size of the resized Element. Must contain attribute newHeight.
   */
  private switchNumberOfDisplayedImages(size: any) {
    const threshold_4_to_9 = 3.5 * this.columnWidth / 100;
    const threshold_9_to_16 = 4.55 * this.columnWidth / 100;

    // display 16 images
    if (size.newHeight / this.screenHeight > threshold_9_to_16 && this.maxShownImages === 9) {
      this.maxShownImages = 16;
      this.reloadImages();
    }

    // display 9 images
    if ((size.newHeight / this.screenHeight > threshold_4_to_9 && this.maxShownImages === 4)
      || (size.newHeight / this.screenHeight < threshold_9_to_16 && this.maxShownImages === 16)) {
      this.maxShownImages = 9;
      this.reloadImages();
    }

    // display 4 images
    if (size.newHeight / this.screenHeight < threshold_4_to_9
      && this.maxShownImages === 9) {
      this.maxShownImages = 4;
      this.reloadImages();
    }
  }

  /**
   * Displays the available images in the grid. Fills empty grid places with emptyImagePlaceholders.
   * @param nOfImages the number of images which should be possible to display.
   * Since the grid is a square the number must be a square number too.
   */
  private displayImages(nOfImages) {
    let columnString = '';
    const nOfColumns = Math.sqrt(nOfImages);
    this.availableImages = [];
    this.emptyImagePlaceholders = [];

    for (let i = 0; i < this.images.length && i < nOfImages; i++) {
      this.availableImages.push(this.images[i]);
    }

    for (let i = this.images.length; i < nOfImages; i++) {
      this.emptyImagePlaceholders.push(i);
    }

    for (let i = 0; i < nOfColumns; i++) {
      columnString += this.columnWidth + 'vh ';
    }
    this.grid_columns_style = columnString;
  }


  /**
   * Handler for click on the left swipe button.
   */
  public handleLeftSwipeBtn() {
    this.swipeLeft();
    this.synchronizationService.sendRealTimeEvent(`GalleryOrderedView${this.data['viewname']}SwipeLeft`);
  }

  /**
   * Handler for click on the left swipe button.
   */
  public handleRightSwipeBtn() {
    this.swipeRight();
    this.synchronizationService.sendRealTimeEvent(`GalleryOrderedView${this.data['viewname']}SwipeRight`);
  }

  /**
   * Swipes to left.
   */
  private swipeLeft() {
    // Possibly do animations here
    this.getPreviousImages();
  }

  /**
   * Swipes to right.
   */
  private swipeRight() {
    // Possibly do animations here
    this.getNextImages();
  }

  /**
   * Callback when a new image should be uploaded by the file chooser.
   * @param event Fired event from the target element.
   */
  public onFileChanged(event) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = (e) => {
        this.galleryService.addImage(this.selectedProject, reader.result as string, file.name,
          images => this.reloadImages(),
          error => { });
        // reset the toggled delete function after an upload
        this.deleteToggle.setOnState(false);
      };

      reader.readAsDataURL(file);
    }
  }

  /**
   * Callback when an image should be deleted. Deletes the image and reloads the widget content.
   * @param image The image which will be deleted.
   */
  onDelete(image: Image) {
    this.galleryService.deleteImage(image,
      response => {
        this.reloadImages();
        this.removeImageFromView(image.id);
      },
      error => { });
  }

  /**
   * Removes all images with the passed id from the view.
   * @param imageId The id of the image which should be removed.
   */
  removeImageFromView(imageId: number) {
    const imgs = document.querySelectorAll('[data-imageid="' + imageId + '"]');
    for (let i = 0; i < imgs.length; i++) {
      imgs[i].parentElement.removeChild(imgs[i]);
    }
  }
}
